import { Link } from 'react-router-dom';
import { useState } from 'react';
import { Box, Button } from '@mui/material';
import { useSelector } from 'react-redux';
import { type MRT_ColumnDef } from 'material-react-table';
import { type RootState } from '../../redux/store';
import ServerTable from '../../components/common/tables/ServerTable';
import { booleanColumn, idColumn } from '../../components/common/tables/tableColumns';
import checkRoutePermissions from '../../routes/appRoutePermissions';
import { type CustomerAccount } from './accountApi';
import { accountTableParams, useAccountsTable } from './useAccountsTable';
import AccountForm from './AccountForm';

const columns: MRT_ColumnDef<CustomerAccount>[] = [
  idColumn(),
  {
    accessorKey: 'account_number',
    header: 'Account no',
    size: 220,
    filterFn: 'contains',
    muiFilterTextFieldProps: { placeholder: 'Contains…' },
  },
  {
    accessorKey: 'account_name',
    header: 'Account Name',
    size: 250,
    filterFn: 'contains',
    Cell: ({ row }) => row.original.account_name || '—',
    muiFilterTextFieldProps: { placeholder: 'Contains…' },
  },
  booleanColumn('alcohol_workflow_enabled', 'Alcohol Workflow'),
  booleanColumn('alcohol_send_to_lab_enabled', 'Alcohol Send to Lab'),
  booleanColumn('manual_override_enabled', 'Manual Override'),
  {
    accessorKey: 'is_archived',
    header: 'Status',
    size: 180,
    filterVariant: 'select',
    filterSelectOptions: [
      { label: 'Active', value: 'false' },
      { label: 'Archived', value: 'true' },
      { label: 'All accounts', value: 'all' },
    ],
    muiFilterTextFieldProps: { placeholder: 'Active' },
    Cell: ({ row }) => (row.original.is_archived ? 'Archived' : 'Active'),
  },
];
/** List and export accounts through shared queries and permitted account actions. */
export default function CustomerAccountsPage() {
  const auth = useSelector((state: RootState) => state.auth);
  const enabled = checkRoutePermissions(auth, { state: 'customer-accounts' }) === true;
  const table = useAccountsTable(enabled, auth.access);
  const [editing, setEditing] = useState<CustomerAccount | null | undefined>(undefined);
  const [showSearch, setShowSearch] = useState(true);
  const [visibility, setVisibility] = useState<Record<string, boolean>>({});
  if (!enabled) return null;
  return (
    <Box sx={{ p: { xs: 1, md: 1.5 }, height: '100dvh', minHeight: 360, boxSizing: 'border-box' }}>
      <ServerTable
        columns={columns}
        title="Customer Accounts"
        recordLabel="accounts"
        exportEndpoint="customer-accounts"
        exportParams={accountTableParams}
        searchPlaceholder="Search account number or name"
        createLabel="Create Account"
        extraActions={<><Button variant="outlined" size="small" component={Link} to="/customer-accounts/imports/new">Import accounts</Button><Button size="small" component={Link} to="/customer-accounts/imports">Import history</Button></>}
        rows={table.rows}
        rowCount={table.rowCount}
        isLoading={table.isLoading}
        hasLoaded={table.hasLoaded}
        error={table.error}
        refresh={table.refresh}
        sorting={table.sorting}
        setSorting={table.onSortingChange}
        columnFilters={table.columnFilters}
        setColumnFilters={table.onColumnFiltersChange}
        pagination={table.pagination}
        setPagination={table.onPaginationChange}
        globalFilter={table.globalFilter}
        setGlobalFilter={table.onGlobalFilterChange}
        rowSelection={table.rowSelection}
        setRowSelection={table.setRowSelection}
        columnVisibility={visibility}
        setColumnVisibility={setVisibility}
        showGlobalFilter={showSearch}
        setShowGlobalFilter={setShowSearch}
        handleRemoveAllFilters={table.clearFilters}
        handleRefreshButton={table.refresh}
        openCreatePage={() => setEditing(null)}
        openEditPage={(row) => setEditing(row.original)}
      />
      {editing !== undefined && (
        <AccountForm
          key={editing?.id ?? 'create'}
          account={editing}
          onClose={() => setEditing(undefined)}
          onSaved={() => {
            setEditing(undefined);
            table.refresh();
          }}
        />
      )}
    </Box>
  );
}
